import { TableColumnMeta } from "ds/components/Table/types";
import { SearchQueryOrderDirection } from "types/generated";

export const MODULE_VERSION_REQUIRED_INPUTS_TABLE_STORAGE_KEY =
  "moduleVersionRequiredInputsTableStorageKey";
export const MODULE_VERSION_OPTIONAL_INPUTS_TABLE_STORAGE_KEY =
  "moduleVersionOptionalInputsTableStorageKey";

export const MODULE_VERSION_REQUIRED_INPUTS_TABLE_FILTERS_STORAGE_KEY =
  "moduleVersionRequiredInputsTableFiltersStorageKey";
export const MODULE_VERSION_OPTIONAL_INPUTS_TABLE_FILTERS_STORAGE_KEY =
  "moduleVersionOptionalInputsTableFiltersStorageKey";

export enum MODULE_VERSION_REQUIRED_INPUTS_COLUMN_ID {
  NAME = "NAME",
  TYPE = "TYPE",
  DESCRIPTION = "DESCRIPTION",
  SETTINGS = "SETTINGS",
}

export enum MODULE_VERSION_OPTIONAL_INPUTS_COLUMN_ID {
  NAME = "NAME",
  TYPE = "TYPE",
  DEFAULT = "DEFAULT",
  DESCRIPTION = "DESCRIPTION",
  SETTINGS = "SETTINGS",
}

const MAX_CELL_WIDTH = 2000;

const NAME_COLUMN_CONFIG: Omit<TableColumnMeta, "id"> = {
  title: "Name",
  minWidth: 180,
  defaultWidth: 300,
  maxWidth: MAX_CELL_WIDTH,
  allowsSorting: true,
  resizable: true,
  canHide: false,
  canPin: true,
  canMove: true,
  configurable: true,
};

const TYPE_COLUMN_CONFIG: Omit<TableColumnMeta, "id"> = {
  title: "Type",
  minWidth: 120,
  defaultWidth: 200,
  maxWidth: MAX_CELL_WIDTH,
  allowsSorting: false,
  resizable: true,
  canHide: true,
  canPin: true,
  canMove: true,
  configurable: true,
};

const DESCRIPTION_COLUMN_CONFIG: Omit<TableColumnMeta, "id"> = {
  title: "Description",
  minWidth: 200,
  defaultWidth: 230,
  maxWidth: MAX_CELL_WIDTH,
  allowsSorting: false,
  resizable: true,
  canHide: true,
  canPin: true,
  canMove: true,
  configurable: true,
};

const SETTINGS_COLUMN_CONFIG: Omit<TableColumnMeta, "id"> = {
  title: "Table settings",
  noHeader: true,
  minWidth: 60,
  defaultWidth: 60,
  maxWidth: 60,
  allowsSorting: false,
  resizable: false,
  canHide: false,
  canPin: false,
  canMove: false,
  configurable: false,
};

export const MODULE_VERSION_REQUIRED_INPUTS_TABLE_COLUMNS_CONFIG: Record<
  MODULE_VERSION_REQUIRED_INPUTS_COLUMN_ID,
  TableColumnMeta
> = {
  [MODULE_VERSION_REQUIRED_INPUTS_COLUMN_ID.NAME]: {
    id: MODULE_VERSION_REQUIRED_INPUTS_COLUMN_ID.NAME,
    ...NAME_COLUMN_CONFIG,
  },
  [MODULE_VERSION_REQUIRED_INPUTS_COLUMN_ID.TYPE]: {
    id: MODULE_VERSION_REQUIRED_INPUTS_COLUMN_ID.TYPE,
    ...TYPE_COLUMN_CONFIG,
  },
  [MODULE_VERSION_REQUIRED_INPUTS_COLUMN_ID.DESCRIPTION]: {
    id: MODULE_VERSION_REQUIRED_INPUTS_COLUMN_ID.DESCRIPTION,
    ...DESCRIPTION_COLUMN_CONFIG,
  },
  [MODULE_VERSION_REQUIRED_INPUTS_COLUMN_ID.SETTINGS]: {
    id: MODULE_VERSION_REQUIRED_INPUTS_COLUMN_ID.SETTINGS,
    ...SETTINGS_COLUMN_CONFIG,
  },
};

export const MODULE_VERSION_OPTIONAL_INPUTS_TABLE_COLUMNS_CONFIG: Record<
  MODULE_VERSION_OPTIONAL_INPUTS_COLUMN_ID,
  TableColumnMeta
> = {
  [MODULE_VERSION_OPTIONAL_INPUTS_COLUMN_ID.NAME]: {
    id: MODULE_VERSION_OPTIONAL_INPUTS_COLUMN_ID.NAME,
    ...NAME_COLUMN_CONFIG,
  },
  [MODULE_VERSION_OPTIONAL_INPUTS_COLUMN_ID.TYPE]: {
    id: MODULE_VERSION_OPTIONAL_INPUTS_COLUMN_ID.TYPE,
    ...TYPE_COLUMN_CONFIG,
  },
  [MODULE_VERSION_OPTIONAL_INPUTS_COLUMN_ID.DEFAULT]: {
    id: MODULE_VERSION_OPTIONAL_INPUTS_COLUMN_ID.DEFAULT,
    title: "Default",
    minWidth: 150,
    defaultWidth: 200,
    maxWidth: MAX_CELL_WIDTH,
    allowsSorting: false,
    resizable: true,
    canHide: true,
    canPin: true,
    canMove: true,
    configurable: true,
  },
  [MODULE_VERSION_OPTIONAL_INPUTS_COLUMN_ID.DESCRIPTION]: {
    id: MODULE_VERSION_OPTIONAL_INPUTS_COLUMN_ID.DESCRIPTION,
    ...DESCRIPTION_COLUMN_CONFIG,
  },
  [MODULE_VERSION_OPTIONAL_INPUTS_COLUMN_ID.SETTINGS]: {
    id: MODULE_VERSION_OPTIONAL_INPUTS_COLUMN_ID.SETTINGS,
    ...SETTINGS_COLUMN_CONFIG,
  },
};

/** Only NAME is sortable; the other entries just satisfy the exhaustive Record. */
export const REQUIRED_INPUTS_SORT_CONFIG: Record<MODULE_VERSION_REQUIRED_INPUTS_COLUMN_ID, string> =
  {
    [MODULE_VERSION_REQUIRED_INPUTS_COLUMN_ID.NAME]: "name",
    [MODULE_VERSION_REQUIRED_INPUTS_COLUMN_ID.TYPE]: "type",
    [MODULE_VERSION_REQUIRED_INPUTS_COLUMN_ID.DESCRIPTION]: "description",
    [MODULE_VERSION_REQUIRED_INPUTS_COLUMN_ID.SETTINGS]: "settings",
  };

/** Only NAME is sortable; the other entries just satisfy the exhaustive Record. */
export const OPTIONAL_INPUTS_SORT_CONFIG: Record<MODULE_VERSION_OPTIONAL_INPUTS_COLUMN_ID, string> =
  {
    [MODULE_VERSION_OPTIONAL_INPUTS_COLUMN_ID.NAME]: "name",
    [MODULE_VERSION_OPTIONAL_INPUTS_COLUMN_ID.TYPE]: "type",
    [MODULE_VERSION_OPTIONAL_INPUTS_COLUMN_ID.DEFAULT]: "default",
    [MODULE_VERSION_OPTIONAL_INPUTS_COLUMN_ID.DESCRIPTION]: "description",
    [MODULE_VERSION_OPTIONAL_INPUTS_COLUMN_ID.SETTINGS]: "settings",
  };

export const REQUIRED_INPUTS_INITIAL_SORT_OPTION =
  REQUIRED_INPUTS_SORT_CONFIG[MODULE_VERSION_REQUIRED_INPUTS_COLUMN_ID.NAME];
export const OPTIONAL_INPUTS_INITIAL_SORT_OPTION =
  OPTIONAL_INPUTS_SORT_CONFIG[MODULE_VERSION_OPTIONAL_INPUTS_COLUMN_ID.NAME];

export const INITIAL_SORT_DIRECTION = SearchQueryOrderDirection.Asc;
